import ClientTags from "../ClientTags/ClientTags";
import * as React from "react";
import {
    ClientStatus,
    DateFormats,
    IACLEmail,
    IClient,
} from "intake_shared/src/types";
import MailIcon from "../../icons/Mail";
import PhoneIcon from "../../icons/Phone";
import HomeIcon from "../../icons/Home";
import {
    displayPhoneNumber,
    findLabelByPracticeId,
    getStateFromAddress,
} from "../../../../utils/utils";
import moment from "../../../../utils/app_moment";
import CalendarIcon from "../../icons/Calendar";
import { Badge } from "../../bits/Badge/Badge";
import {
    getColorStylesForClientStatus,
    getV2BadgePropsByPractice,
} from "../../../../constants";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import { Button } from "../../bits/Button/Button";
import CopyIcon from "../../icons/Copy";
import { cn, convertArrayToString, copyToClipboard } from "../../../helpers";
import { getGuardianFullName } from "../../../../utils/guardian";
import { isClientElderly, isClientMinor } from "../../../../utils/age";
import PauseIcon from "../../icons/Pause";
import { CacheContext } from "../../../../contexts/CacheContext";
import { AclContext } from "../../../../contexts/AclContext";
import FilterButton from "../../bits/FilterButton/FilterButton";
import store from "../../../../store";
import styles from "./ClientInfo.module.scss";
import { MetaBox } from "../../bits/MetaBox/MetaBox";
import { TSaveClient } from "../clientUiTypes";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatWebformStep } from "./webformStep";
import LeaStatus from "../communication/LeaConversation/LeaStatus";

interface ClientInfoProps {
    client: IClient;
    acl?: IACLEmail;
    isInactive?: boolean;
    onSaveClient: TSaveClient;
    locked?: boolean;
}

export default function ClientInfo({
    client,
    acl,
    isInactive,
    onSaveClient,
    locked = false,
}: ClientInfoProps) {
    const timeStamp = (() => {
        if (!client?.status_history) return null;

        const inquiryStatus = client.status_history[ClientStatus.INQUIRY];
        const incompleteInquiryStatus =
            client.status_history[ClientStatus.INCOMPLETE_INQUIRY];

        const safeToDate = (value: any): Date | null => {
            if (!value) return null;

            if (value instanceof Date) return value;
            if (typeof value.toDate === "function") {
                return value.toDate();
            }

            return null;
        };

        const inquiryDate = safeToDate(inquiryStatus);
        if (inquiryDate) return inquiryDate;

        const incompleteInquiryDate = safeToDate(incompleteInquiryStatus);
        if (incompleteInquiryDate) return incompleteInquiryDate;

        return null;
    })();

    const convertedDate = moment(timeStamp).format(
        DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
    );

    const clientDOB = moment(client?.date_of_birth).format(
        DateFormats.DAY_MONTH_YEAR_FORMAT
    );

    const isMinor = isClientMinor(client?.date_of_birth);
    const isElderly = isClientElderly(client?.date_of_birth);
    const pausedAppt = client?.confirmed_bookings.find((c) => c.is_paused);

    const olc = React.useContext(CacheContext);
    const aclContext = React.useContext(AclContext);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const intakeOfficerColors = aclContext?.intakeConfig?.intakeOfficerColors || {};

    const getAssigneeColorStyle = (email: string) => {
        const configuredColor = intakeOfficerColors[email];
        return configuredColor ? { borderColor: configuredColor } : undefined;
    };

    const getPracticesForClient = () => {
        const practices = client?.confirmed_bookings?.map((appointment) => {
            const practiceId =
                appointment.practice_id ||
                (process.env.REACT_APP_MANTLE_PRACTICE_ID as string);

            const practiceLabel = findLabelByPracticeId(
                practiceId,
                allDepartmentsAndServices
            );

            return practiceLabel || "HT";
        });

        return Array.from(new Set(practices));
    };

    const clientPractices = getPracticesForClient();

    return (
        <div className={styles.clientInfo}>
            <div className={styles.clientNameWrapper}>
                <p className={styles.name}>
                    {`${client.first_name} ${client.last_name}`}
                    {(isMinor || isElderly) && (
                        <Badge tone="info" content={isMinor ? "Minor" : "Elderly"} />
                    )}
                    {isInactive && <Badge tone="neutral" content="Inactive" />}
                    {pausedAppt && (
                        <OverlayTrigger
                            placement="bottom"
                            trigger={["hover", "focus"]}
                            overlay={(props) => (
                                <Tooltip id={`tooltip-paused-appt`} {...props}>
                                    {`Client has a paused appointment with ${pausedAppt.clinician_display_name}`}
                                </Tooltip>
                            )}
                        >
                            <span>
                                <PauseIcon />
                            </span>
                        </OverlayTrigger>
                    )}
                </p>
                <span>
                    <Badge
                        content={client.status}
                        textColor="#ffffff"
                        bgColor={
                            getColorStylesForClientStatus(client.status).bgColor
                        }
                    />
                </span>

                {client.is_blacklisted && (
                    <span>
                        <Badge tone="danger" content="Blacklisted" />
                    </span>
                )}
            </div>

            <div className={styles.clientMeta}>
                <div className={styles.metaRow}>
                    <MetaBox icon={<MailIcon />} value={client?.email || "--"} />
                    <MetaBox
                        icon={<PhoneIcon />}
                        value={
                            client?.phone_no
                                ? displayPhoneNumber(client.phone_no)
                                : "--"
                        }
                    />
                    {(client?.state || getStateFromAddress(client?.address)) && (
                        <MetaBox
                            icon={<HomeIcon />}
                            value={
                                client?.state || getStateFromAddress(client?.address)
                            }
                        />
                    )}
                    {client?.date_of_birth && (
                        <MetaBox
                            icon={<CalendarIcon />}
                            value={`${clientDOB} (${moment().diff(
                                client.date_of_birth,
                                "years",
                                false
                            )} YRS)`}
                        />
                    )}

                    <Button
                        variant="secondary"
                        className={styles.copyButton}
                        title="Copy name, email and phone"
                        iconLeft={<CopyIcon />}
                        onClick={() =>
                            copyToClipboard(
                                convertArrayToString([
                                    `Name: ${client.first_name} ${client.last_name}`,
                                    client.email ? `Email: ${client.email}` : "",
                                    `${
                                        client.phone_no
                                            ? `Phone no: ${client?.phone_no}`
                                            : ""
                                    }`,
                                ])
                            )
                        }
                    >
                        Copy
                    </Button>
                </div>
                <div className={styles.metaRow}>
                    <MetaBox label="Sex" value={client?.sex || "U"} />
                    {client?.preferred_name && (
                        <MetaBox
                            label="Preferred name"
                            value={client.preferred_name}
                        />
                    )}
                    <MetaBox
                        label="Inquiry method"
                        value={client?.inquiry_method || "--"}
                    />
                    {client?.webform_step && (
                        <MetaBox
                            label="Webform step"
                            value={formatWebformStep(client?.webform_step)}
                        />
                    )}
                    <MetaBox label="Inquiry date" value={convertedDate} />
                    <MetaBox
                        label="Credit cards on file"
                        value={
                            client.payment_card_count !== undefined
                                ? client.payment_card_count
                                : "--"
                        }
                    />
                    <>
                        {client[ClientStatus.FOLLOWUP]?.start_date && (
                            <>
                                <MetaBox
                                    label="Follow-up start"
                                    value={
                                        moment(
                                            client[ClientStatus.FOLLOWUP]?.start_date
                                        ).format(
                                            DateFormats.DAY_MONTH_YEAR_FORMAT
                                        ) || "--"
                                    }
                                />
                                <MetaBox
                                    label="Follow-up end"
                                    value={
                                        client[ClientStatus.FOLLOWUP]?.ending_date
                                            ? moment(
                                                  client[ClientStatus.FOLLOWUP]
                                                      ?.ending_date
                                              ).format(
                                                  DateFormats.DAY_MONTH_YEAR_FORMAT
                                              )
                                            : "--"
                                    }
                                />
                                <MetaBox
                                    label="Status after follow-up"
                                    value={
                                        client[ClientStatus.FOLLOWUP]
                                            ?.status_after_end_date || "--"
                                    }
                                />
                            </>
                        )}
                    </>
                    <div className={`${styles.metaBox} ${styles.consentDropdown}`}>
                        <span className={styles.metaLabel}>Consent to SMS:</span>
                        <span className={styles.metaValue}>
                            {locked ? (
                                <FilterButton
                                    className={cn(styles.filterButton, {
                                        [styles.no]: client.consent_to_sms === "No",
                                    })}
                                    text={client?.consent_to_sms || "--"}
                                />
                            ) : (
                                <Dropdown>
                                    <Dropdown.Trigger>
                                        <FilterButton
                                            className={cn(styles.filterButton, {
                                                [styles.no]:
                                                    client.consent_to_sms === "No",
                                            })}
                                            text={client?.consent_to_sms || "--"}
                                        />
                                    </Dropdown.Trigger>
                                    <Dropdown.Content
                                        className={styles.dropdownContent}
                                    >
                                        {["Yes", "No"].map((item) => (
                                            <Dropdown.Item
                                                key={`consent-${item}`}
                                                onClick={async () => {
                                                    await store.updateClientData({
                                                        client_id: client.id,
                                                        changes: {
                                                            consent_to_sms: item,
                                                        },
                                                        last_updated_by: acl?.email!,
                                                    });
                                                    onSaveClient({
                                                        ...client,
                                                        consent_to_sms: item,
                                                    });
                                                }}
                                            >
                                                {item}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Content>
                                </Dropdown>
                            )}
                        </span>
                    </div>

                    <LeaStatus client={client} locked={locked} />

                    {client.assigned_to && (
                        <div className={styles.metaBox}>
                            <span className={styles.metaLabel}>Assigned to:</span>
                            <span className={styles.metaValue}>
                                {client.assigned_to ? (
                                    <span
                                        className={styles.assigneeWithColor}
                                        style={getAssigneeColorStyle(
                                            client.assigned_to
                                        )}
                                    >
                                        {client.assigned_to}
                                    </span>
                                ) : (
                                    "--"
                                )}
                                {client.secondary_assignee && (
                                    <>
                                        <span
                                            className={styles.assigneeWithColor}
                                            style={getAssigneeColorStyle(
                                                client.secondary_assignee
                                            )}
                                        >
                                            {client.secondary_assignee}
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>
                    )}

                    {clientPractices && clientPractices.length > 0 && (
                        <MetaBox
                            label="Practices"
                            value={
                                <div className={styles.practicesWrapper}>
                                    {clientPractices.map((practice, index) => (
                                        <Badge
                                            key={`practice-${index}`}
                                            content={practice}
                                            textColor={
                                                getV2BadgePropsByPractice(practice)
                                                    .textColor
                                            }
                                            bgColor={
                                                getV2BadgePropsByPractice(practice)
                                                    .bgColor
                                            }
                                        />
                                    ))}
                                </div>
                            }
                        />
                    )}
                    {client.client_is_b2b && (
                        <MetaBox
                            label="B2B"
                            value={client.client_is_b2b ? "Yes" : "No"}
                        />
                    )}
                    {client?.primary_parent && isMinor && (
                        <MetaBox
                            label="Primary parent/guardian"
                            value={getGuardianFullName(
                                client.primary_parent,
                                client.primary_parent_last_name
                            )}
                        />
                    )}
                    {client?.other_parent && isMinor && (
                        <MetaBox
                            label="Other parent/guardian"
                            value={getGuardianFullName(
                                client.other_parent,
                                client.other_parent_last_name
                            )}
                        />
                    )}
                    {client?.other_parent_email && isMinor && (
                        <MetaBox
                            label="Other parent/guardian email"
                            value={client.other_parent_email}
                        />
                    )}
                    {client?.other_parent_phone && isMinor && (
                        <MetaBox
                            label="Other parent/guardian phone"
                            value={client.other_parent_phone}
                        />
                    )}
                    {client?.has_no_other_parent_guardian && isMinor && (
                        <MetaBox
                            label="No other parent/guardian reason"
                            value={
                                client.no_other_parent_guardian_reason?.trim()
                                    ? client.no_other_parent_guardian_reason
                                    : "--"
                            }
                        />
                    )}
                </div>
                <ClientTags
                    key={client.id}
                    clientId={client.id}
                    isInactive={isInactive}
                    locked={locked}
                />
            </div>
        </div>
    );
}
